import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { eur } from "@/lib/api";
import { toast } from "sonner";
import SmartImage from "@/components/SmartImage";
import { SIZES } from "@/lib/images";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const [loaded, setLoaded] = useState(false);

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;
  const outOfStock = product.stock <= 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    addItem(product, 1);
    toast.success(`${product.name} ajouté au panier`);
  };

  return (
    <Link
      to={`/produit/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group flex flex-col gap-3 animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="aspect-[1/1.18] overflow-hidden bg-sand relative">
        {!loaded && <div className="absolute inset-0 z-10 animate-pulse bg-stone" />}
        <SmartImage
          src={product.image}
          alt={product.name}
          sizes={SIZES.productGrid}
          // Les premières vignettes du catalogue sont visibles d'emblée (et
          // souvent l'élément LCP de la page) : les charger en priorité, et
          // laisser les suivantes en chargement différé.
          priority={index < 2}
          onLoad={() => setLoaded(true)}
          className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105"
        />
        {discount > 0 && (
          <span className="absolute top-3.5 left-3.5 bg-copper text-white text-[11px] tracking-wide px-2.5 py-1 font-medium">
            −{discount}%
          </span>
        )}
        {outOfStock && (
          <span className="absolute top-3.5 right-3.5 bg-ink/90 text-cream text-[10px] uppercase tracking-wider px-2 py-1">
            Complet
          </span>
        )}
        {!outOfStock && (
          <>
            {/* Desktop: bar revealed on hover only; pointer-events disabled while hidden so it
                never intercepts taps on touch devices (which have no real hover state). */}
            <button
              data-testid={`quick-add-${product.slug}`}
              onClick={handleAdd}
              className="hidden sm:block absolute left-3.5 right-3.5 bottom-3.5 bg-ink/92 backdrop-blur-sm text-cream text-center py-3 text-[12px] uppercase tracking-[0.15em] opacity-0 translate-y-2 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto hover:bg-gold hover:text-ink"
            >
              Ajouter au panier
            </button>
            {/* Mobile: small, always-visible icon button in a corner so quick-add has its own
                tap target, separate from the rest of the card which opens the product page. */}
            <button
              data-testid={`quick-add-mobile-${product.slug}`}
              onClick={handleAdd}
              aria-label="Ajouter au panier"
              className="sm:hidden absolute bottom-3 right-3 w-11 h-11 flex items-center justify-center rounded-full bg-ink/90 backdrop-blur-sm text-cream shadow-[0_2px_10px_rgba(0,0,0,0.35)] ring-1 ring-cream/15 active:bg-gold active:text-ink active:ring-gold active:scale-90 transition-all duration-150"
            >
              <Plus className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </>
        )}
      </div>
      <div className="flex flex-col pt-4">
        <p className="text-[11px] uppercase tracking-[0.15em] text-ash mb-1.5">{product.brand || product.category}</p>
        <h3 className="font-serif text-xl font-medium text-ink leading-tight mb-3 transition-colors group-hover:text-copper">{product.name}</h3>
        <div className="flex items-baseline gap-2.5 mt-auto">
          <span className="text-[18px] font-medium text-ink">{eur(product.price)}</span>
          {product.original_price && (
            <span className="text-sm text-ash line-through">{eur(product.original_price)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export const ProductCardSkeleton = () => (
  <div className="flex flex-col gap-3">
    <div className="aspect-[1/1.18] bg-stone animate-pulse" />
    <div className="h-3 w-16 bg-stone animate-pulse" />
    <div className="h-5 w-3/4 bg-stone animate-pulse" />
    <div className="h-3 w-20 bg-stone animate-pulse" />
  </div>
);
